import React, { useState, useEffect } from 'react';
import Icon from '../components/ui/Icon';
import API from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { usePopup } from '../context/PopupContext';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import useFormDraft from '../hooks/useFormDraft';
import './AddStudent.css'; // Reuse AddStudent styles

const EditStudent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showConfirm } = usePopup();
  const [form, setForm] = useState({
    name: '', email: '', roll_number: '',
    branch: '', phone: '',
    cgpa: '', arrears: '', year_of_study: '',
    placement_batch_year: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);
  const [draftBannerDismissed, setDraftBannerDismissed] = useState(false);

  // audit AD-10: same unsaved-changes protection as Add Student.
  useUnsavedChangesGuard(dirty);
  const { initialDraft, savedAt, clearDraft } = useFormDraft(`edit_student_${id}`, form, { enabled: dirty });
  const showDraftBanner = !!initialDraft && !draftBannerDismissed && !dirty;
  const restoreDraft = () => { setForm(f => ({ ...f, ...initialDraft.data })); setDirty(true); setDraftBannerDismissed(true); };
  const discardDraft = () => { clearDraft(); setDraftBannerDismissed(true); };

  useEffect(() => {
    fetchStudent();
  }, [id]);

  const fetchStudent = async () => {
    try {
      const res = await API.get(`/students/${id}`);
      const s = res.data;
      setForm({
        name: s.name || '',
        email: s.email || '',
        roll_number: s.roll_number || '',
        branch: s.branch || '',
        phone: s.phone || '',
        // audit AD-06: these four are numeric fields where 0 is a real, meaningful value (arrears=0
        // means "no arrears") — `||` treated it the same as a missing value and blanked the field.
        // `??` only falls back to '' for null/undefined, leaving a genuine 0 alone. The five string
        // fields above are untouched: '' is already the correct empty state for them.
        cgpa: s.cgpa ?? '',
        arrears: s.arrears ?? '',
        year_of_study: s.year_of_study ?? '',
        placement_batch_year: s.placement_batch_year ?? ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch student details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setDirty(true);
    setError(''); setSuccess('');
  };

  const handleCancel = async () => {
    if (dirty) {
      const ok = await showConfirm('Discard changes?', 'You have unsaved changes to this student. Leaving now will discard them.');
      if (!ok) return;
    }
    clearDraft();
    navigate('/admin/students');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await API.put(`/students/${id}`, form);
      setSuccess('Student updated successfully!');
      clearDraft();
      setDirty(false);
      setTimeout(() => navigate('/admin/students'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update student');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-state">Loading student data...</div>;

  return (
    <div className="add-student-page-content">
      <div className="top-bar">
        <div>
          <h2>Edit Student</h2>
          <p>Update student profile information</p>
        </div>
        <button className="back-btn" onClick={handleCancel}>
          <Icon name="chevron-left" size={16} /> Back to Students
        </button>
      </div>

      <div className="add-card">
        {showDraftBanner && (
          <div className="alert warning" role="status">
            <div style={{ fontWeight: 'bold' }}>You have unsaved edits from {new Date(initialDraft.savedAt).toLocaleString()}.</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
              <button type="button" className="btn-submit" onClick={restoreDraft}>Restore draft</button>
              <button type="button" className="btn-cancel" onClick={discardDraft}>Discard</button>
            </div>
          </div>
        )}
        {error && <div className="alert error">{error}</div>}
        {success && <div className="alert success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Full Name *</label>
              <input name="name" placeholder="Enter full name"
                value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Roll Number</label>
              <input name="roll_number" placeholder="e.g. 21CS001"
                value={form.roll_number} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Email * (Read-only)</label>
              <input type="email" name="email" value={form.email} disabled />
            </div>
            <div className="form-group">
              <label>Branch</label>
              <select name="branch" value={form.branch} onChange={handleChange}>
                <option value="">Select branch</option>
                <option value="CSE">CSE</option>
                <option value="ECE">ECE</option>
                <option value="EEE">EEE</option>
                <option value="IT">IT</option>
                <option value="MECH">MECH</option>
                <option value="CIVIL">CIVIL</option>
              </select>
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input name="phone" placeholder="Enter phone number"
                value={form.phone} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>CGPA</label>
              <input type="number" step="0.01" name="cgpa" placeholder="e.g. 8.5"
                value={form.cgpa} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Arrears</label>
              <input type="number" name="arrears" placeholder="0"
                value={form.arrears} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Year of Study</label>
              <select name="year_of_study" value={form.year_of_study} onChange={handleChange}>
                <option value="">Select year</option>
                <option value="1">1st Year</option>
                <option value="2">2nd Year</option>
                <option value="3">3rd Year</option>
                <option value="4">4th Year</option>
              </select>
            </div>
            <div className="form-group">
              <label>Placement Batch Year</label>
              <input name="placement_batch_year" placeholder="e.g. 2026"
                value={form.placement_batch_year} onChange={handleChange} />
            </div>
          </div>
          <div className="form-actions">
            {savedAt && <span style={{ fontSize: '0.8em', color: 'var(--z-muted)', alignSelf: 'center', marginRight: 'auto' }}>Draft autosaved {new Date(savedAt).toLocaleTimeString()}</span>}
            <button type="button" className="btn-cancel" onClick={handleCancel}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStudent;
